<template>
  <div class="page-domain">
    <PageHeader>
      <div class="heading">
        <h1>
          <strong class="name">{{ company.name }}</strong>
          / <router-link :to="{name: 'company-domains'}">Domains</router-link>
          / {{ domain }}
        </h1>
      </div>
    </PageHeader>

    <div class="page-columns">
      <div class="col main">
        <div class="page-content">
          <div class="domain-tiles">
            <div class="tile">
              <Sprite name="invoice"/>
              <span v-if="domainData.autorenewEnabled">Auto Renew:<br>Enabled</span>
              <span v-else>Auto Renew:<br>Disabled</span>
            </div>

            <div class="tile">
              <Sprite name="history"/>
              <span>Domain Expires:<br>{{expiryDate}}</span>
            </div>

            <div class="tile">
              <Sprite name="credit-cards"/>
              <span v-if="domainData.locked">Domain Status:<br>Locked</span>
              <span v-else>Domain Status:<br>Unlocked</span>
            </div>
          </div>

          <div class="domain-actions">
            <div class="domain-action">
              <h3>Connect your domain</h3>
              <p>
                Each website you manage can have <strong>one</strong> domain name
                connected to it.
              </p>

              <p>
                <strong v-html="connectionMessage"></strong>
              </p>

              <select v-model="siteToConnect">
                <option disabled selected>
                  Select a site
                </option>
                <option
                  v-for="site in sites"
                  :key="site.id"
                  :value="site.id">
                  {{site.name}}
                </option>
              </select>

              <div>
                <router-link
                  v-if="siteToConnect"
                  class="btn btn-connect-domain is-tertiary"
                  :to="{
                    name: 'connect-company-domain',
                    params: {siteId: siteToConnect},
                    query: {domain: this.domain}
                  }"
                  >Connect Now</router-link>
              </div>
            </div>

            <div class="domain-action">
              <h3>URL Forwarding</h3>

              <Notification
                v-if="notification.active"
                :withinOverlay="true"
                :content="notification.content"
                :type="notification.type"/>

              <div v-if="hasHost">
                <p>This domain name is currently forwarding to <strong>{{domainForwarding.forwardsTo}}</strong>.</p>
                <router-link
                  class="btn is-tertiary"
                  :to="{
                    name: 'edit-domain-forwarding',
                    params: {
                      companyId: companyId,
                      domain: domain,
                      host: domainForwarding.host
                    }
                  }"
                  >Edit URL</router-link
                >

                <Btn subtle @click="deleteForwarding">Remove</Btn>
              </div>

              <div v-else>
                <p>If you'd like to connect multiple domain names to a single
                website, please set up <strong>URL Forwarding</strong>.</p>
                <p>URL forwarding has not been set up for this domain.</p>

                <router-link
                  class="btn is-tertiary"
                  :to="{
                    name: 'add-domain-forwarding',
                    params: {
                      companyId: companyId,
                      domain: domain
                    }
                  }"
                  >Add URL Forwarding</router-link
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <div>
            <h1>My Domain Name</h1>
            <p>This domain expires <strong>{{futureInvoiceDate}}</strong>.</p>
            <p>We'll automatically renew it each year using your default payment method to keep your site running smoothly. If we can't charge your card, we'll send you an invoice by email.</p>
            <p>Failing that, your domain will expire and disconnect from your site.</p>
          </div>
        </PageSummary>
      </div>
    </div>

    <router-view :companyName="company.name" />
  </div>
</template>

<script>
import moment from 'moment'

import page from '@/mixins/page'
import pagination from '@/mixins/pagination'
import notification from '@/mixins/notification'

import {mapState} from 'vuex'

import {readCompanyData} from '@/lib/companies'
import {readCompanySitesData} from '@/lib/sites'

import {
  readCompanyDomainData,
  readCompanyDomainForwardingsData,
  checkDomainConnectionData,
  deleteCompanyDomainForwardingData
} from '@/lib/domains'

import PageHeader from '@/components/PageHeader'
import PageSummary from '@/components/PageSummary'
import Btn from '@/components/Btn'
import Sprite from '@/components/Sprite'

export default {
  name: 'company-domains',

  mixins: [page, pagination, notification],

  props: {
    companyId: {
      type: String,
      required: true
    },

    domain: {
      type: String,
      required: true
    }
  },

  components: {
    PageHeader,
    PageSummary,
    Btn,
    Sprite
  },

  data() {
    return {
      company: {},
      domainData: {},
      sites: [],
      domainForwarding: {},
      connectionMessage: '',
      hasHost: false,
      siteToConnect: false,
      domainDataLoaded: false
    }
  },

  methods: {
    getData() {
      readCompanyData(this.companyId).then(({data}) => {
        this.company = data
      })

      readCompanyDomainData(this.companyId, this.domain).then(({data}) => {
        this.domainData = data

        this.getConnectionData()

        this.domainDataLoaded = true
      })

      readCompanySitesData(this.companyId).then(({data}) => {
        this.sites = data
      })

      this.getForwardingData()
    },

    getConnectionData() {
      checkDomainConnectionData(this.companyId, this.domain).then(({data}) => {
        this.connectionMessage = data.message
      })
    },

    getForwardingData() {
      readCompanyDomainForwardingsData(this.companyId, this.domain).then(
        ({data}) => {
          let isEmpty = Object.keys(data).length === 0

          if (!isEmpty) {
            this.domainForwarding = data.urlForwarding[0]
            this.hasHost = true
          } else {
            this.hasHost = false
          }
        }
      )
    },

    deleteForwarding() {
      deleteCompanyDomainForwardingData(this.companyId, this.domain, this.domainForwarding.host)
        .then(({data}) => {
          this.triggerNotification('success', 'forwarding', 'removed')

          window.setTimeout(() => {
            this.getForwardingData()
          }, this.closeDelay)
        })
    }
  },

  computed: {
    ...mapState(['currentUser']),

    title() {
      return `Managing ${this.domain}`
    },

    expiryDate() {
      return moment(this.domainData.expireDate).format('dddd DD MMMM, YYYY')
    },

    futureInvoiceDate() {
      return moment(this.domainData.expireDate).fromNow()
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass" scoped>
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-domain
  @extend %page

  .domain-actions
    display: flex

  .domain-action
    @extend %card
    flex-basis: 50%
    position: relative
    margin: 0 $gap-small

  h3
    margin-top: 0

  .btn
    margin-right: $gap-small

  .btn-connect-domain
    margin-top: $gap-small

.domain-tiles
  width: 100%
  display: flex
  flex-wrap: wrap
  align-items: flex-start
  justify-content: center
  text-align: center
  text-transform: uppercase
  padding: $gap-small 0
  margin-bottom: $gap-medium

  .tile
    flex-basis: calc(#{percentage(1/3)} - #{$gap-small*3})
    margin: $gap-small
    display: flex
    align-items: center
    flex-direction: column
    font-size: 14px
    font-weight: bold
    opacity: 0.2

    .sprite
      fill: rgba(0, 0, 0, .5)
      width: 40px
      height: 40px
      margin-bottom: $gap-medium
      transition: all .2s ease-in-out
</style>
